import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Loader2, AlertTriangle, Search, ChevronDown } from 'lucide-react';
import { instrumentAPI } from '../../../../../services/api/instrumentAPI';

const SIDES = [
  { value: 'buy', label: 'Long (Buy)' },
  { value: 'sell', label: 'Short (Sell)' },
];

const AddTradeModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedUser,
  processing,
}) => {
  const [instrument, setInstrument] = useState('');
  const [side, setSide] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [timestamp, setTimestamp] = useState('');

  const [instruments, setInstruments] = useState([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  const [error, setError] = useState('');
  const [minSize, setMinSize] = useState(null);
  const [maxSize, setMaxSize] = useState(null);
  const [qtyStep, setQtyStep] = useState(null);
  const [priceDecimals, setPriceDecimals] = useState(null);
  const [instSearch, setInstSearch] = useState('');
  const [instDropdownOpen, setInstDropdownOpen] = useState(false);
  const instDropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setInstrument('');
      setSide('buy');
      setQuantity('');
      setPrice('');
      setTimestamp('');
      setError('');
      setMinSize(null);
      setMaxSize(null);
      setQtyStep(null);
      setPriceDecimals(null);
      setInstSearch('');
      setInstDropdownOpen(false);
      fetchInstruments();
    }
  }, [isOpen]);

  useEffect(() => {
    if (instrument) {
      const inst = instruments.find(i => i.symbol === instrument);
      if (inst) {
        setMinSize(parseFloat(inst.min_Order_Size) || 0);
        setMaxSize(parseFloat(inst.max_Order_Size) || 0);
        setQtyStep(parseFloat(inst.order_Quantity_Step) || 0);
        const decimals = parseInt(inst.decimals, 10);
        setPriceDecimals(!isNaN(decimals) && decimals >= 0 ? decimals : null);
      } else {
        setMinSize(null);
        setMaxSize(null);
        setQtyStep(null);
        setPriceDecimals(null);
      }
    }
  }, [instrument, instruments]);

  const fetchInstruments = async () => {
    try {
      setInstrumentsLoading(true);
      const response = await instrumentAPI.getAll();
      const list = response?.data?.data?.data || response?.data?.data || response?.data || [];
      setInstruments(Array.isArray(list) ? list : []);
    } catch {
      setInstruments([]);
    } finally {
      setInstrumentsLoading(false);
    }
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (instDropdownRef.current && !instDropdownRef.current.contains(e.target)) {
        setInstDropdownOpen(false);
      }
    };
    if (instDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [instDropdownOpen]);

  // Filtered instruments
  const filteredInstruments = instruments.filter((inst) => {
    if (!instSearch.trim()) return true;
    const q = instSearch.toLowerCase();
    return (
      inst.symbol.toLowerCase().includes(q) ||
      (inst.description && inst.description.toLowerCase().includes(q)) ||
      (inst.type && inst.type.toLowerCase().includes(q))
    );
  });

  // Selected instrument display
  const selectedInst = instruments.find(i => i.symbol === instrument);

  const validate = () => {
    if (!instrument) return 'Please select an instrument';
    const qty = Number(quantity);
    if (!quantity || qty <= 0) return 'Quantity must be positive';
    if (minSize != null && qty < minSize) return 'Min order size is ' + minSize;
    if (maxSize != null && qty > maxSize) return 'Max order size is ' + maxSize;
    // Step / multiplier check: (qty - minSize) must be divisible by step
    if (qtyStep != null && qtyStep > 0 && minSize != null) {
      const remainder = (qty - minSize) % qtyStep;
      if (Math.abs(remainder) > 0.0001) {
        return 'Quantity must be in increments of ' + qtyStep + ' (e.g. ' + minSize + ', ' + (minSize + qtyStep) + ', ' + (minSize + qtyStep * 2) + ')';
      }
    }
    if (!price || Number(price) <= 0) return 'Entry price is required';
    return null;
  };

  const handleConfirm = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    onConfirm({
      userId: selectedUser?.id,
      symbol: instrument,
      side,
      quantity: Number(quantity),
      price: +Number(price).toFixed(decimals),
      timestamp: timestamp || undefined,
    });
  };

  if (!isOpen) return null;

  const decimals = priceDecimals != null ? priceDecimals : 5;
  const priceStep = priceDecimals != null ? String(Math.pow(10, -priceDecimals)) : 'any';

  // Format constraint hint
  const constraintHint = () => {
    if (minSize == null) return '';
    let hint = 'Min: ' + minSize;
    if (maxSize != null) hint += ' | Max: ' + maxSize;
    if (qtyStep != null && qtyStep > 0) hint += ' | Step: ' + qtyStep;
    return hint;
  };

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h3><Plus size={15} /> Add Trade</h3>
          <button className="pm-modal-close" onClick={onClose} disabled={processing}>
            <X size={14} />
          </button>
        </div>

        <div className="pm-modal-body">
          {selectedUser && (
            <div style={{ fontSize: '0.75rem', color: 'var(--pm-text-sec)', marginBottom: '0.5rem', fontWeight: 600 }}>
              User: {selectedUser.email || selectedUser.label || selectedUser.id}
            </div>
          )}

          <div className="pm-modal-field">
            <label>Instrument</label>
            {instrumentsLoading ? (
              <div style={{ padding: '8px 0', color: 'var(--pm-text-muted)', fontSize: '0.78rem' }}>Loading instruments...</div>
            ) : (
              <div className="pm-inst-dropdown" ref={instDropdownRef}>
                <div
                  className={'pm-inst-dropdown-trigger' + (instDropdownOpen ? ' open' : '')}
                  onClick={() => { if (!processing) setInstDropdownOpen(!instDropdownOpen); }}
                >
                  {selectedInst ? (
                    <span className="pm-inst-dropdown-value">
                      <strong>{selectedInst.symbol}</strong>
                      {selectedInst.description && (
                        <span style={{ color: 'var(--pm-text-muted)', marginLeft: '6px', fontWeight: 400 }}>
                          {selectedInst.description}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--pm-text-muted)' }}>Search or select instrument...</span>
                  )}
                  <ChevronDown size={14} className={'pm-inst-dropdown-arrow' + (instDropdownOpen ? ' open' : '')} />
                </div>

                {instDropdownOpen && (
                  <div className="pm-inst-dropdown-menu">
                    <div className="pm-inst-dropdown-search">
                      <Search size={12} />
                      <input
                        type="text"
                        value={instSearch}
                        onChange={(e) => setInstSearch(e.target.value)}
                        placeholder="Search by symbol, name, or type..."
                        autoFocus
                        disabled={processing}
                      />
                    </div>
                    <div className="pm-inst-dropdown-list">
                      {filteredInstruments.length === 0 ? (
                        <div className="pm-inst-dropdown-empty">No instruments found</div>
                      ) : (
                        filteredInstruments.map((inst) => (
                          <div
                            key={inst.symbol}
                            className={'pm-inst-dropdown-item' + (instrument === inst.symbol ? ' selected' : '')}
                            onClick={() => {
                              setInstrument(inst.symbol);
                              setInstSearch('');
                              setInstDropdownOpen(false);
                              setError('');
                            }}
                          >
                            <span className="pm-inst-dropdown-symbol">{inst.symbol}</span>
                            {inst.description && (
                              <span className="pm-inst-dropdown-desc">{inst.description}</span>
                            )}
                            {inst.type && (
                              <span className="pm-inst-dropdown-type">{inst.type}</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pm-modal-field">
            <label>Side</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {SIDES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={'pm-btn ' + (side === s.value ? 'pm-btn--primary' : 'pm-btn--ghost')}
                  style={{ fontSize: '0.75rem', padding: '6px 14px', flex: 1 }}
                  onClick={() => setSide(s.value)}
                  disabled={processing}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pm-modal-row">
            <div className="pm-modal-field" style={{ marginBottom: 0 }}>
              <label>
                Quantity
                {constraintHint() && (
                  <span style={{ fontWeight: 400, fontSize: '0.62rem', color: 'var(--pm-text-muted)', marginLeft: '4px' }}>
                    ({constraintHint()})
                  </span>
                )}
              </label>
              <input
                type="number"
                min={minSize || 0}
                max={maxSize || undefined}
                step={qtyStep || 'any'}
                value={quantity}
                onChange={(e) => { setQuantity(e.target.value); setError(''); }}
                placeholder="0.00"
                disabled={processing}
              />
            </div>
            <div className="pm-modal-field" style={{ marginBottom: 0 }}>
              <label>Entry Price *</label>
              <input
                type="number"
                min="0"
                step={priceStep}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={'0.' + '0'.repeat(decimals)}
                disabled={processing}
              />
            </div>
          </div>

          <div className="pm-modal-field">
            <label>
              Timestamp <span style={{ fontWeight: 400, fontSize: '0.65rem', color: 'var(--pm-text-muted)' }}>(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              disabled={processing}
            />
          </div>

          <div className="pm-modal-hint">
            Leverage is auto-resolved from user's group. Set TP/SL via Modify after adding.
          </div>

          {error && (
            <div className="pm-warning">
              <AlertTriangle size={12} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn pm-btn--ghost" onClick={onClose} disabled={processing} style={{ fontSize: '0.78rem' }}>
            Cancel
          </button>
          <button
            className="pm-btn pm-btn--primary"
            onClick={handleConfirm}
            disabled={processing || !instrument || !quantity || !price}
            style={{ fontSize: '0.78rem' }}
          >
            {processing ? (
              <><Loader2 size={12} className="pm-spin" /> Adding</>
            ) : (
              <><Plus size={12} /> Add Trade</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTradeModal;
